import React from 'react';
import { Redirect } from 'react-router';

import { withStyles } from '@material-ui/core/styles';
import CircularProgress from '@material-ui/core/CircularProgress';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import Link from '@material-ui/core/Link';
import PeopleIcon from '@material-ui/icons/People';
import Typography from "@material-ui/core/Typography";
import VisibilityIcon from '@material-ui/icons/VisibilityOutlined';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOffOutlined';

import Api from '../../../../../../common/api'
import Auth from '../../../../../../common/auth'
import Local from '../../../../../../common/local'
import Util from '../../../../../../common/util';
import Recaptcha from '../../../../../../common/components/recaptcha';

import CurrentUser from '../../../../shared/components/current-user';
import Heading from '../../../../shared/components/heading';
import LeftRail from '../../../../shared/components/left-rail';
import PageHeader from '../../../../shared/components/page-header';
import StandardInput from '../../../../shared/components/gn-standard-input';
import withTranslations from '../../../../../../common/components/with-translations';

const styles = (theme) => ({
  view: {
    ...theme.internalContainer,
    [theme.breakpoints.up('md')]: {
      padding: theme.internalContainer._themeMdPadding
    },
  },
  button: {
    borderRadius: '9999px',
    fontWeight: '600',
    fontSize: '16px',
    height: '55px',
    width: '100%',
  },
  buttonHover: {
    '&:hover': {
      backgroundColor: 'var(--button-hover-color) !important'
    },
  },
  buttonControls: {
    display: "flex",
    justifyContent: "center",
    marginTop: "15px"
  },
  noHeaderContainer: {
    padding: '20px 20px 0px 20px',
    maxWidth: '1200px',
    margin: '0 auto 50px auto'
  },
  stepTitle: {
    display: "flex",
    justifyContent: "center",
    fontWeight: '600',
    fontSize: '1.2rem',
    marginBottom: '10px',
  }
});

class StepTwoFactor extends React.Component {

  constructor(props) {

    super(props);

    this.state = {
      processing: false,
      code: '',
      validation: {
        code: null
      }
    };

    this.api = new Api();
    this.generateOnInput = this.generateOnInput.bind(this);
    this.onError = this.onError.bind(this);
    this.onLogin = this.onLogin.bind(this);
    this.onSubmitPressed = this.onSubmitPressed.bind(this);
    this.startLogin = this.startLogin.bind(this);
  }

  generateOnInput(stateKey) {

    return function (e) {

      this.setState({
        [stateKey]: e.target.value,
        validation: { code: null }
      });
    }.bind(this);
  }

  onError(err) {

    this.setState({ processing: false });
    this.props.onError(err);
  }

  async onSubmitPressed(e) {

    if (e.key === 'Enter') {
      await this.startLogin();
    }
  }

  async onLogin(recaptcha) {
    const { t } = this.props;
    const {
      username,
      password
    } = this.props;
    const { code } = this.state;

    const validation = {
      code: code === '' ? t('stepTwoFactor.requiredCodeError') : false
    };

    if (code.length !== 6) {
      validation.code = t('stepTwoFactor.codeLengthError');
    }

    if (Object.values(validation).some((v) => { return v !== false; })) {
      return this.setState({ validation });
    }

    this.setState({ processing: true });

    try {
      const response = await this.api.post(
        `/user/authenticate${this.props.clientId ? `?clientId=${this.props.clientId}` : ''}`,
        {
          username,
          password,
          code,
          recaptcha,
          parameters: Util.toCoreQueryObject(this.props.query)
        }
      );

      if (response.code === 201) {
        this.props.postMessageRaw({ event: '2fa', success: true });
        return this.props.onLoginSuccess(response.content.token);
      }
      else if (response.code >= 400) {
        this.props.postMessageRaw({ event: '2fa', success: false });
        if (response.code === 400) {
          return this.onError({
            error: (
              <div>{response.content.message}</div>
            )
          });
        }
        else if (response.code === 401) {
          return this.onError({
            error: t('stepTwoFactor.userNotFoundError')
          });
        }
        else if (response.code === 423) {
          return this.onError({
            error: t('stepTwoFactor.accountLockedError')
          });
        }
        else if (response.code === 417) {
          return this.onError({
            error: t('stepTwoFactor.codeExpiredError')
          });
        }
        else if (response.code === 426) {
          return this.props.onUpgradeAccount(username);
        }
        else if (response.code === 428) {
          return this.props.onRequiresReconciliation(username);
        }
      }
    }
    catch (e) {
      return this.onError({ error: e.toString() });
    }
  }

  async startLogin() {
    if (window.globals && window.globals.gidRecaptchaKey) {
      const that = this;
      window.grecaptcha.ready(function () {
        window.grecaptcha.execute(window.globals.gidRecaptchaKey, { action: 'submit' }).then(async function (token) {
          await that.onLogin(token);
        });
      });
    } else {
      await this.onLogin();
    }
  }

  render() {
    const { t } = this.props;
    const containerStyle =
      this.props.query.noHeader ?
        this.props.classes.noHeaderContainer :
        this.props.classes.view;

    const emailInput = (
      <Grid item xs={12} md={12}>
        <StandardInput
          pattern="\\d+"
          style={{ marginBottom: '15px' }}
          error={this.state.validation.code}
          attribute="code"
          label={t('stepTwoFactor.twoFactorCodeLabel')}
          labelWidth={100}
          focus={true}
          onChange={this.generateOnInput('code')}
          onKeyUp={this.onSubmitPressed} />
      </Grid>
    );

    const displayableContent = this.props.application && this.props.application.displayable && this.props.application.displayable.content
      ? this.props.application.displayable.content
      : {};

    const buttonColor = displayableContent.buttonColor || (this.props.query.buttonColor && `#${this.props.query.buttonColor}`);
    const buttonHoverColor = displayableContent.buttonHoverColor || (this.props.query.buttonHoverColor && `#${this.props.query.buttonHoverColor}`);

    const buttonStyle = {
      ...(buttonColor && { backgroundColor: buttonColor }),
      ...(buttonHoverColor && { '--button-hover-color': buttonHoverColor }),
    };

    const buttonClassName = buttonHoverColor ?
      `${this.props.classes.button} ${this.props.classes.buttonHover}` :
      this.props.classes.button;

    const recaptcha = (window.globals && window.globals.gidRecaptchaKey && !this.props.query.noForgot) ?
      <Recaptcha textClassname={this.props.classes.recaptchaText} /> : null;

    const bottomControls = (
      <Grid className={this.props.classes.buttonControls} item xs={12}>
        <Button style={buttonStyle} className={buttonClassName} color="primary" onClick={this.startLogin} variant="contained" id="login">
          {this.state.processing ?
            (<CircularProgress size={14} color="inherit" />)
            : t('stepTwoFactor.continueButton')
          }
        </Button>
      </Grid>
    );

    const stepTitle = this.props.query.showSteps === 'true' ? (
      <Grid className={this.props.classes.stepTitle} item xs={12}>
        <Typography className={this.props.classes.stepTitle}>
          {t('stepTwoFactor.stepTitle')}
        </Typography>
      </Grid>
    ) : null;

    return (
      <Grid container spacing={0} className={containerStyle} >
        {stepTitle}
        {emailInput}
        {bottomControls}
      </Grid>
    );
  }
}

export default withTranslations(withStyles(styles)(StepTwoFactor));
